import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { usePayment } from "../context/PaymentContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import '../styles/Profile.css';
import { FaCog, FaUser, FaTrophy, FaStar, FaGift, FaHistory, FaGamepad, FaPercent, FaShieldAlt, FaTelegram, FaEnvelope } from "react-icons/fa";
import VerificationModal from './VerificationModal';
import LevelInfoModal from './LevelInfoModal';
import TelegramLinkModal from './TelegramLinkModal';

import { getApiUrl } from "../utils/config";
const API_URL = getApiUrl();

const Profile = () => {
    const { user, showNotification } = useAuth();
    const {
        selectedCurrency,
        balanceUsd,
        balanceRub,
        balanceEur,
        balanceBrl,
        balanceUsdt,
        bonusBalanceUsd,
        bonusBalanceRub,
        bonusBalanceEur,
        bonusBalanceBrl,
        bonusBalanceUsdt,
        getCurrentBalance,
        getCurrentBonusBalance,
        fetchBalance,
        fetchProfile,
        openProfileModal,
        openAccountHistoryModal,
        openGameHistoryModal,
        profile,
        isBalanceLoaded
    } = usePayment();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [userLevel, setUserLevel] = useState(null);
    const [levelsConfig, setLevelsConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [showLevelInfoModal, setShowLevelInfoModal] = useState(false);
    const [showTelegramLinkModal, setShowTelegramLinkModal] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);

    // Анимированный счетчик для прогресса
    const useAnimatedNumber = (target, duration = 1000) => {
        const [current, setCurrent] = useState(0);

        useEffect(() => {
            if (target === 0) {
                setCurrent(0);
                return;
            }

            const startTime = Date.now();
            const startValue = current;

            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);

                const easeOutQuart = 1 - Math.pow(1 - progress, 4);
                const value = Math.floor(startValue + (target - startValue) * easeOutQuart);

                setCurrent(value);

                if (progress < 1) {
                    requestAnimationFrame(animate);
                }
            };

            requestAnimationFrame(animate);
        }, [target, duration]);

        return current;
    };

    // Получение информации об уровне пользователя
    const fetchUserLevel = async () => {
        try {
            const response = await fetch(`${API_URL}/api/user-level`, {
                method: 'GET',
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setUserLevel(data);
            } else {
                setError('Ошибка при загрузке информации об уровне');
            }
        } catch (err) {
            console.error('Ошибка при получении уровня:', err);
            setError('Ошибка при загрузке информации об уровне');
        }
    };

    // Получение конфигурации всех уровней
    const fetchLevelsConfig = async () => {
        try {
            const response = await fetch(`${API_URL}/api/levels`, {
                method: 'GET',
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setLevelsConfig(data);
            } else {
                console.error('Ошибка при загрузке конфигурации уровней');
            }
        } catch (err) {
            console.error('Ошибка при получении конфигурации уровней:', err);
        }
    };

    // Загрузка всех данных
    const loadData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                fetchUserLevel(),
                fetchLevelsConfig()
            ]);
        } catch (err) {
            console.error('Ошибка при загрузке данных:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isBalanceLoaded) {
            loadData();
            fetchProfile();
        }
    }, [isBalanceLoaded]);

    const animatedProgress = useAnimatedNumber(userLevel?.progress || 0, 1500);
    const animatedExperience = useAnimatedNumber(userLevel?.experiencePoints || 0, 2000);

    const getCurrencySymbol = (currency) => {
        switch (currency) {
            case 'USD': return '$';
            case 'RUB': return '₽';
            case 'EUR': return '€';
            case 'BRL': return 'R$';
            case 'USDT': return '₮';
            default: return '$';
        }
    };

    const handleSendEmailVerification = async () => {
        if (sendingEmail) return;
        
        // Проверка наличия email (для аккаунтов через Telegram)
        if (!profile?.email) {
            showNotification('Email не указан. Пожалуйста, добавьте email в настройках профиля', 'error');
            openProfileModal();
            return;
        }
        
        setSendingEmail(true);
        try {
            const response = await fetch(`${API_URL}/api/email-verification/send`, {
                method: 'POST',
                credentials: 'include'
            });
            const data = await response.json();
            if (response.ok) {
                showNotification(data.message || 'Письмо с подтверждением отправлено на ваш email', 'success');
            } else {
                showNotification(data.message || 'Ошибка при отправке письма подтверждения', 'error');
            }
        } catch (error) {
            console.error('Ошибка отправки письма подтверждения:', error);
            showNotification('Произошла ошибка при отправке письма подтверждения', 'error');
        } finally {
            setSendingEmail(false);
        }
    };

    // Безопасное получение баланса
    const getSafeBalance = () => {
        const balance = getCurrentBalance();
        console.log('Profile Debug - getCurrentBalance():', balance, 'type:', typeof balance);
        console.log('Profile Debug - selectedCurrency:', selectedCurrency);
        console.log('Profile Debug - balances:', { balanceUsd, balanceRub, balanceEur, balanceBrl, balanceUsdt });
        console.log('Profile Debug - isBalanceLoaded:', isBalanceLoaded);
        return typeof balance === 'number' ? balance : 0;
    };

    const getSafeBonusBalance = () => {
        const balance = getCurrentBonusBalance();
        console.log('Profile Debug - getCurrentBonusBalance():', balance, 'type:', typeof balance);
        return typeof balance === 'number' ? balance : 0;
    };

    if (loading || !isBalanceLoaded || !levelsConfig) {
        return (
            <div className="profile-container">
                <div className="profile-loading">
                    <div className="loading-spinner"></div>
                    <p>{t('Загрузка профиля...')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="profile-container">
                <div className="profile-error">
                    <p>{error}</p>
                    <button onClick={loadData} className="retry-button">
                        {t('Попробовать снова')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="profile-container">
            <div className="profile-content">
                {/* Заголовок профиля */}
                <div className="profile-header">
                    <div className="profile-header-left">
                        <div className="profile-avatar">
                            {(profile?.passportPhoto || user?.avatar_url) ? (
                                <img src={profile?.passportPhoto || user?.avatar_url} alt="Аватар" />
                            ) : (
                                <FaUser />
                            )}
                        </div>
                        <div className="profile-basic-info">
                            <h1>{profile?.username || user?.username || 'Пользователь'}</h1>
                            <p className="profile-id">ID: {profile?.MyuserId || user?.MyuserId}</p>
                        </div>
                    </div>
                    <div className="profile-header-buttons">
                        {!profile?.emailVerified && profile?.email && (
                            <button
                                className="settings-button email-verify-button"
                                onClick={handleSendEmailVerification}
                                title={sendingEmail ? 'Отправка...' : 'Подтвердить email'}
                                disabled={sendingEmail}
                            >
                                <FaEnvelope />
                                <span>{sendingEmail ? 'Отправка...' : 'Подтвердить email'}</span>
                            </button>
                        )}
                        <button
                            className="settings-button"
                            onClick={openProfileModal}
                            title="Настройки профиля"
                        >
                            <FaCog />
                        </button>
                    </div>
                </div>

                {/* Секция верификации и Telegram */}
                <div className="profile-actions-section">
                    <button
                        className="history-button verification-action-button"
                        onClick={() => {
                            console.log('Нажата кнопка Верификация');
                            setShowVerificationModal(true);
                        }}
                    >
                        <div className="history-button-icon">
                            <FaShieldAlt />
                        </div>
                        <div className="history-button-content">
                            <span className="history-button-title">Верификация</span>
                            <span className="history-button-description">Подтвердите свой аккаунт</span>
                        </div>
                    </button>

                    <button
                        className="history-button telegram-action-button"
                        onClick={() => {
                            console.log('Нажата кнопка Telegram');
                            setShowTelegramLinkModal(true);
                        }}
                    >
                        <div className="history-button-icon">
                            <FaTelegram />
                        </div>
                        <div className="history-button-content">
                            <span className="history-button-title">Telegram</span>
                            <span className="history-button-description">Привяжите для бонусов</span>
                        </div>
                    </button>
                </div>

                {/* Информация об уровне */}
                {userLevel && (
                    <div className="level-section">
                        <div className="level-card">
                            {/* Заголовок секции */}
                            <div className="level-section-header">
                                <h3 className="level-section-title">
                                    {t('Ваш уровень')}
                                    <span className="current-level-number">{userLevel.currentLevel.level}</span>
                                </h3>
                                <button
                                    className="level-info-btn"
                                    onClick={() => setShowLevelInfoModal(true)}
                                    aria-label="Информация о системе уровней"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="12" cy="12" r="10" stroke="#bfc3d1" strokeWidth="2" fill="rgba(255,255,255,0.1)" />
                                        <rect x="11" y="10" width="2" height="6" rx="1" fill="#bfc3d1" />
                                        <rect x="11" y="7" width="2" height="2" rx="1" fill="#bfc3d1" />
                                    </svg>
                                </button>
                            </div>

                            {/* Основная информация об уровне */}
                            <div className="level-main-info">
                                <div className="level-badge">
                                    <div className="level-icon-wrapper" style={{ borderColor: userLevel.currentLevel.color }}>
                                        <div className="level-icon" style={{ color: userLevel.currentLevel.color }}>
                                            {typeof userLevel.currentLevel.icon === 'string' && userLevel.currentLevel.icon.endsWith('.webp') ? (
                                                <img src={userLevel.currentLevel.icon} alt={userLevel.currentLevel.name} style={{ width: 50, height: 50, objectFit: 'contain' }} />
                                            ) : (
                                                <span className="level-emoji">{userLevel.currentLevel.icon}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="level-details">
                                        <h2 className="level-name" style={{ color: userLevel.currentLevel.color }}>
                                            {userLevel.currentLevel.name}
                                        </h2>
                                    </div>
                                </div>
                            </div>

                            {/* Статистика */}
                            <div className="level-stats-grid">
                                <div className="level-stat-card">
                                    <div className="stat-icon-wrapper">
                                        <FaStar className="stat-icon" />
                                    </div>
                                    <div className="stat-content">
                                        <span className="stat-value">{animatedExperience.toLocaleString()}</span>
                                        <span className="stat-label">{t('Очки опыта')}</span>
                                    </div>
                                </div>

                                <div className="level-stat-card">
                                    <div className="stat-icon-wrapper">
                                        <FaTrophy className="stat-icon" />
                                    </div>
                                    <div className="stat-content">
                                        <span className="stat-value">{userLevel.currentLevel.cashbackPercent}%</span>
                                        <span className="stat-label">{t('Кешбек')}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Прогресс до следующего уровня */}
                            {userLevel.nextLevel && (
                                <div className="level-progress-section">
                                    <div className="progress-header-compact">
                                        <div className="progress-info-compact">
                                            <span className="progress-label-compact">
                                                {t('До уровня')} {userLevel.nextLevel.level} "{userLevel.nextLevel.name}"
                                            </span>
                                            <span className="progress-points-compact">
                                                {userLevel.pointsToNext} {t('очков')}
                                            </span>
                                        </div>
                                        <span className="progress-percentage-compact">{animatedProgress}%</span>
                                    </div>

                                    <div className="progress-bar-container-compact">
                                        <div
                                            className="progress-bar-fill-compact"
                                            style={{
                                                width: `${animatedProgress}%`,
                                                backgroundColor: userLevel.nextLevel.color,
                                                boxShadow: `0 0 12px ${userLevel.nextLevel.color}30`
                                            }}
                                        />
                                        <div className="progress-bar-bg-compact" />
                                    </div>
                                </div>
                            )}

                            {/* Максимальный уровень */}
                            {!userLevel.nextLevel && (
                                <div className="max-level-achievement">
                                    <div className="achievement-icon">
                                        <FaTrophy />
                                    </div>
                                    <div className="achievement-text">
                                        <h4>{t('Максимальный уровень!')}</h4>
                                        <p>{t('Вы достигли высшего уровня в системе')}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Вертикальный прогресс бар по уровням */}
                        <div className="level-progression-bar">
                            <div className="progression-header">
                                <h4>{t('Прогресс по уровням')}</h4>
                            </div>
                            <div className="progression-levels">
                                {levelsConfig?.levels?.map((level, index) => {
                                    const isAchieved = userLevel.experiencePoints >= level.minPoints;
                                    const isCurrent = level.level === userLevel.currentLevel.level;

                                    return (
                                        <div
                                            key={level.level}
                                            className={`progression-level ${isAchieved ? 'achieved' : 'locked'} ${isCurrent ? 'current' : ''}`}
                                        >
                                            <div className="progression-level-icon" style={{
                                                borderColor: isAchieved ? level.color : '#3a3b4a',
                                                backgroundColor: isAchieved ? level.color + '20' : 'rgba(255,255,255,0.05)'
                                            }}>
                                                <span className="progression-emoji" style={{
                                                    opacity: isAchieved ? 1 : 0.3
                                                }}>
                                                    {typeof level.icon === 'string' && level.icon.endsWith('.webp') ? (
                                                        <img src={level.icon} alt={level.name} style={{ width: 50, height: 50, objectFit: 'contain' }} />
                                                    ) : (
                                                        level.icon
                                                    )}
                                                </span>
                                            </div>
                                            <div className="progression-level-info">
                                                <div className="progression-level-header">
                                                    <div className="progression-level-title">
                                                        <div className="progression-level-number" style={{
                                                            backgroundColor: isAchieved ? level.color : '#3a3b4a',
                                                            borderColor: isAchieved ? level.color : '#3a3b4a'
                                                        }}>
                                                            {level.level}
                                                        </div>
                                                        <div className="progression-level-name" style={{
                                                            color: isAchieved ? level.color : '#6b7280'
                                                        }}>
                                                            {level.name}
                                                        </div>
                                                    </div>
                                                    <div className="progression-level-points">
                                                        {level.minPoints} {t('очков')}
                                                    </div>
                                                </div>
                                                <div className="progression-level-reward">
                                                    {level.cashbackPercent}% {t('кешбек')}
                                                </div>
                                            </div>
                                            {index < (levelsConfig?.levels?.length || 0) - 1 && (
                                                <div className={`progression-connector ${isAchieved ? 'achieved' : 'locked'}`} />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Кнопки истории */}
                <div className="profile-history-section">
                    <button
                        className="history-button"
                        onClick={openAccountHistoryModal}
                    >
                        <div className="history-button-icon">
                            <FaHistory />
                        </div>
                        <div className="history-button-content">
                            <span className="history-button-title">{t('История счёта')}</span>
                            <span className="history-button-description">{t('Пополнения и выводы')}</span>
                        </div>
                    </button>

                    <button
                        className="history-button"
                        onClick={openGameHistoryModal}
                    >
                        <div className="history-button-icon">
                            <FaGamepad />
                        </div>
                        <div className="history-button-content">
                            <span className="history-button-title">{t('История игр')}</span>
                            <span className="history-button-description">{t('Ваши игровые сессии')}</span>
                        </div>
                    </button>

                    <button
                        className="history-button"
                        onClick={() => navigate('/bonuses')}
                    >
                        <div className="history-button-icon">
                            <FaGift />
                        </div>
                        <div className="history-button-content">
                            <span className="history-button-title">{t('Бонусы')}</span>
                            <span className="history-button-description">{t('Ваши бонусы и акции')}</span>
                        </div>
                    </button>

                    <button
                        className="history-button"
                        onClick={() => navigate('/referral')}
                    >
                        <div className="history-button-icon">
                            <FaPercent />
                        </div>
                        <div className="history-button-content">
                            <span className="history-button-title">{t('Партнёрам')}</span>
                            <span className="history-button-description">{t('Реферальная программа')}</span>
                        </div>
                    </button>
                </div>


            </div>

            {/* Модальное окно верификации */}
            {showVerificationModal && (
                <VerificationModal onClose={() => setShowVerificationModal(false)} />
            )}

            {/* Модальное окно информации об уровнях */}
            {showLevelInfoModal && (
                <LevelInfoModal onClose={() => setShowLevelInfoModal(false)} />
            )}

            {/* Модальное окно привязки Telegram */}
            {showTelegramLinkModal && (
                <TelegramLinkModal onClose={() => setShowTelegramLinkModal(false)} />
            )}
        </div>
    );
};

export default Profile;